'use client'

import { trackMetaPixelEvent } from '@/components/MetaPixel'
import { Spinner } from '@/components/ui/spinner'
import { useAuthModal } from '@/hooks/useAuthModal'
import { analytics } from '@/lib/analytics'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { useState } from 'react'
import Modal from '../Modal/Modal'
import SignInForm from './SignInForm'
import SignUpForm from './SignUpForm'
import ThirdPartyAuth from './ThirdPartyAuth'

export function AuthModal() {
  const { isOpen, onClose } = useAuthModal()
  const [isSignIn, setIsSignIn] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { refetch: refreshUser } = useGetUserQuery()

  const handleAuthSuccess = async (method: string, isSignUp: boolean = false) => {
    await refreshUser()
    setIsLoading(false)
    onClose()

    // Track login success
    analytics.userLifecycle.loginSuccess(method)

    // Track Meta Pixel events
    if (isSignUp) {
      trackMetaPixelEvent('CompleteRegistration', { method })
      analytics.userLifecycle.signupComplete(method)
    } else {
      trackMetaPixelEvent('Login', { method })
    }
  }

  const handleAuthAction = (method: string) => {
    setIsLoading(true)
    analytics.userLifecycle.loginMethodClicked(method)
  }

  const handleAuthError = (error: string, method: string) => {
    setIsLoading(false)
    analytics.userLifecycle.loginError(error, method)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="relative isolate flex w-[502px] max-w-[95vw] flex-col items-center gap-8 rounded-3xl border border-white/30 bg-[#171717] p-4 sm:gap-12 sm:p-[36px_24px_32px]">
        {isLoading && <Spinner variant="default" size={40} overlay={true} />}
        <div className="relative z-10 flex w-full flex-col items-center gap-8">
          <h2
            className="font-hanken-grotesk w-full bg-clip-text text-center text-2xl font-bold leading-none text-transparent sm:w-[454px] sm:text-[36px]"
            style={{
              background:
                'radial-gradient(50% 50% at 50% 50%, #FFFFFF 30%, rgba(255, 255, 255, 0.5) 84.77%)',
              WebkitBackgroundClip: 'text',
              backdropFilter: 'blur(6px)',
            }}
          >
            {isSignIn ? 'Welcome Back' : 'Welcome to Sprites'}
          </h2>

          <ThirdPartyAuth className="w-full max-w-xs sm:w-[364px] sm:max-w-none" />

          <div className="flex w-full items-center justify-center gap-4 opacity-80 sm:w-auto">
            <div className="h-[1px] flex-1 bg-white" />
            <span className="font-hanken-grotesk text-base font-medium text-white">or</span>
            <div className="h-[1px] flex-1 bg-white" />
          </div>

          <div className="flex w-full max-w-xs flex-col gap-8 sm:w-[364px] sm:max-w-none">
            {isSignIn ? (
              <SignInForm
                onSignInSuccessAction={handleAuthSuccess}
                onSubmitStart={handleAuthAction}
                onError={handleAuthError}
              />
            ) : (
              <SignUpForm
                onSignUpSuccessAction={handleAuthSuccess}
                onSubmitStart={handleAuthAction}
                onError={handleAuthError}
              />
            )}

            <div className="text-left">
              <span className="font-hanken-grotesk text-base font-medium tracking-[-0.011em] text-white/70">
                {isSignIn ? "Don't have an account? " : 'Already have an account? '}
                <button
                  onClick={() => setIsSignIn(!isSignIn)}
                  className="text-white/70 transition-colors hover:text-white"
                >
                  {isSignIn ? 'Sign up' : 'Sign in'}
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
